/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { ConfigEditorEdit } from "../../ConfigEditorEdit";
import { Panel } from "../../../../../ui/components/Panel";
import { useStateWithEvent } from "../../../../../ui/hooks";
import { TreeView } from "../../../../../ui/components/Tree/TreeView";
import { useTreeState } from "../../../../../ui/components/Tree/useTreeState";
import { ITreeNode } from "../../../../../ui/components/Tree/TreeViewTypes";
import { ISchemaConstObjectOptsGroup } from "@adapptio/lib-blueprint";

function getCategoriesTree(
	categories: ISchemaConstObjectOptsGroup[],
	t: (domain: string, term: string) => string
): ITreeNode[] {
	return categories.map((category) => ({
		id: category.id,
		label: t("editor", category.label),
		icon: getCategoryIcon(category.id),
		selectable: true,
		metaData: {
			category
		}
	}));
}

function getCategoryIcon(categoryId: string): string {
	switch (categoryId) {
		case "ssh":
			return "mdi/key";
		case "system":
			return "mdi/cog";
		case "constants":
			return "mdi/variable";
		case "services":
			return "mdi/cloud";
		case "visual":
			return "mdi/palette";
		default:
			return "mdi/circle-outline";
	}
}

export interface IConfigEditorEditOutlineProps {
	className?: string;
	view: ConfigEditorEdit;
}

export const ConfigEditorEditOutline: React.FC<IConfigEditorEditOutlineProps> = ({ className, view }) => {
	const baseClass = BEM("edt-config-editor-edit-outline");
	const t = useTranslate();

	const [ treeState, setTreeState ] = useTreeState({ nodes: {} });

	const categories = useMemo(() => view.getCategories(), [ view ]);

	const selectedCategory = useStateWithEvent(view.onSelectionChange, () => view.getSelectedCategory());

	const selections = useMemo(() => [ selectedCategory ], [ selectedCategory ]);

	const treeItems = useMemo(
		() => getCategoriesTree(categories, t),
		[ categories, t ]
	);

	const handleSelect = useCallback(
		(node: ITreeNode) => {
			view.selectCategory(String(node.id));
		},
		[ view ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<Panel className={baseClass.toClassName()}>
			<TreeView
				items={treeItems}
				state={treeState}
				selections={selections}
				onStateUpdate={setTreeState}
				onNodeSelect={handleSelect}
			/>
		</Panel>
	);
};
